/**
 * Trovva — Lead Scoring
 *
 * Pure, deterministic composite score for leads (0-100). Mirrors
 * the same philosophy as agentScoring.js: weighted dimensions, null
 * tolerance, transparent breakdown.
 *
 * Dimensions:
 *   - recency        (30%) — how recently the lead was touched
 *   - progression    (25%) — where they are in the pipeline
 *   - engagement     (20%) — task completion + note activity
 *   - source_quality (15%) — weight of their acquisition source
 *   - data_richness  (10%) — completeness of their profile (email,
 *                            phone, budget, timeline, preapproval)
 *
 * Returns null for leads with no signal at all.
 */

export const LEAD_SCORE_WEIGHTS = {
  recency:        0.30,
  progression:    0.25,
  engagement:     0.20,
  source_quality: 0.15,
  data_richness:  0.10,
};

// Source weight: how likely a source converts to a closed deal.
// Values here tuned by gut — adjust after real data comes in.
const SOURCE_WEIGHTS = {
  referral: 100, past_client: 100, repeat: 95, sphere: 90,
  open_house: 70, sign_call: 65, website: 60,
  zillow: 55, realtor: 55, redfin: 50,
  facebook: 45, instagram: 40, tiktok: 35,
  google_ads: 50, facebook_ads: 45,
  csv_import: 40, cold: 25, purchased: 20,
};

const STAGE_PROGRESS = {
  new: 20, contacted: 40, qualified: 60,
  nurturing: 50, under_contract: 85, closed: 100, lost: 0,
};

/** Compute composite score 0-100, or null if nothing to score. */
export function computeLeadScore(lead, { now = Date.now() } = {}) {
  if (!lead) return null;

  const dims = [];

  // ── Recency ──
  // 100 if contacted in last 24h, 0 if > 30 days / never.
  if (lead.last_contacted || lead.updated_at) {
    const last = new Date(lead.last_contacted || lead.updated_at).getTime();
    const daysSince = Math.max(0, (now - last) / 86400000);
    const recencyV = daysSince >= 30 ? 0 : Math.round(100 - (daysSince * (100 / 30)));
    dims.push({ w: LEAD_SCORE_WEIGHTS.recency, v: recencyV });
  }

  // ── Progression ──
  if (lead.lead_status && STAGE_PROGRESS[lead.lead_status] != null) {
    dims.push({ w: LEAD_SCORE_WEIGHTS.progression, v: STAGE_PROGRESS[lead.lead_status] });
  }

  // ── Engagement ──
  // Based on task completion + note count in last 30 days. If the
  // caller didn't pass aggregates, fall back to a flat 40 for any
  // lead with non-new status.
  if (lead._engagement != null) {
    dims.push({ w: LEAD_SCORE_WEIGHTS.engagement, v: Math.max(0, Math.min(100, lead._engagement)) });
  } else if (lead.lead_status && lead.lead_status !== 'new') {
    dims.push({ w: LEAD_SCORE_WEIGHTS.engagement, v: 40 });
  }

  // ── Source quality ──
  if (lead.lead_source) {
    const key = String(lead.lead_source).toLowerCase().replace(/\s+/g, '_');
    const weight = SOURCE_WEIGHTS[key] ?? 50;
    dims.push({ w: LEAD_SCORE_WEIGHTS.source_quality, v: weight });
  }

  // ── Data richness ──
  const richnessFields = [lead.email, lead.phone, lead.budget, lead.timeline, lead.preapproved];
  const filled = richnessFields.filter(f => f != null && f !== '').length;
  const richness = Math.round((filled / richnessFields.length) * 100);
  dims.push({ w: LEAD_SCORE_WEIGHTS.data_richness, v: richness });

  if (!dims.length) return null;
  const totalW = dims.reduce((a, d) => a + d.w, 0);
  const score = dims.reduce((a, d) => a + d.v * d.w, 0) / totalW;
  return Math.round(score);
}

/**
 * Mark a lead's staleness level based on last_contacted.
 * Returns: 'fresh' | 'warm' | 'cool' | 'cold'
 */
export function stalenessOf(lead, { now = Date.now() } = {}) {
  if (!lead?.last_contacted && !lead?.updated_at) return 'cold';
  const last = new Date(lead.last_contacted || lead.updated_at).getTime();
  const days = (now - last) / 86400000;
  if (days < 3)  return 'fresh';
  if (days < 7)  return 'warm';
  if (days < 14) return 'cool';
  return 'cold';
}

export function daysSinceContact(lead, { now = Date.now() } = {}) {
  if (!lead?.last_contacted && !lead?.updated_at) return null;
  const last = new Date(lead.last_contacted || lead.updated_at).getTime();
  return Math.max(0, Math.floor((now - last) / 86400000));
}
